import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { useProfile } from '../../contexts/ProfileContext';

/**
 * Helper function to preload an image
 */
const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        img.src = src;
    });
};

/**
 * ProfileImage Component
 * 
 * Enterprise-level profile image component with:
 * - Automatic cache busting
 * - Error handling with fallback
 * - Loading states
 * - Multiple size variants
 * - Accessibility support
 */
const ProfileImage = ({
    imageUrl,
    alt = 'Profile',
    size = 'md',
    className = '',
    cacheBust = false
}) => {
    const { imageVersion } = useProfile(); // Get version from context
    
    const [imageSrc, setImageSrc] = useState(null);
    const [imageError, setImageError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Size variants
    const sizeClasses = {
        xs: 'w-6 h-6',
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16',
        '2xl': 'w-24 h-24',
        '3xl': 'w-32 h-32'
    };

    const iconSizes = {
        xs: 'w-3 h-3',
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6',
        xl: 'w-8 h-8',
        '2xl': 'w-12 h-12',
        '3xl': 'w-16 h-16'
    };

    // Use context imageVersion for automatic cache busting
    const finalImageUrl = imageUrl && cacheBust 
        ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${imageVersion}`
        : imageUrl;

    useEffect(() => {
        const loadImage = async () => {
            if (!imageUrl) {
                setImageSrc(null);
                setImageError(false);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setImageError(false);

            try {
                // Preload image to detect errors before rendering
                await preloadImage(finalImageUrl);
                
                setImageSrc(finalImageUrl);
                setImageError(false);
            } catch (error) {
                console.error('Failed to load profile image:', error);
                setImageError(true);
            } finally {
                setIsLoading(false);
            }
        };

        loadImage();
    }, [imageUrl, imageVersion, cacheBust, finalImageUrl]);

    const handleImageError = () => {
        setImageError(true);
    };

    const sizeClass = sizeClasses[size] || sizeClasses.md;
    const iconSize = iconSizes[size] || iconSizes.md;
    const roundedClass = 'rounded-full';

    // Show loading state
    if (isLoading) {
        return (
            <div
                className={`${sizeClass} ${roundedClass} bg-gray-200 animate-pulse ${className}`}
                role="img"
                aria-label="Loading profile image"
            />
        );
    }

    // Show image if loaded successfully
    if (imageSrc && !imageError) {
        return (
            <img
                src={imageSrc}
                alt={alt}
                className={`${sizeClass} ${roundedClass} object-cover border border-gray-200 ${className}`}
                onError={handleImageError}
                loading="lazy"
            />
        );
    }

    // Show fallback if enabled
    return (
        <div
            className={`${sizeClass} ${roundedClass} bg-gray-100 flex items-center justify-center border border-gray-200 ${className}`}
            role="img"
            aria-label={alt}
        >
            <User className={`${iconSize} text-gray-400`} />
        </div>
    );
};

export default ProfileImage;
